import { Component, HostListener, inject, signal, computed, OnInit, OnDestroy, AnimationCallbackEvent } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ApplicationService, RegisteredAppResponse, ContentCreatorInfo } from '../../core/services/application';
import { UserService, User } from '../../core/services/user';
import { AuthService } from '../../core/services/auth';

type AppPlatform = 'ios' | 'android' | 'both';

@Component({
  selector: 'app-applications',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './applications.html',
  styleUrl: './applications.css'
})
export class ApplicationsComponent implements OnInit, OnDestroy {
  private appService = inject(ApplicationService);
  private userService = inject(UserService);
  private auth = inject(AuthService);
  private fb = inject(FormBuilder);

  apps = signal<RegisteredAppResponse[]>([]);
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);

  // Filters (API: skip, limit, is_active)
  activeFilter = signal<'all' | 'active' | 'inactive'>('all');
  filterOpen = signal(false);
  filterClosing = signal(false);
  private filterCloseTimer: ReturnType<typeof setTimeout> | null = null;

  // Cached counts when we last loaded "all" — so other tabs still show correct numbers when active/inactive is selected
  cachedTotalCount = signal<number>(0);
  cachedActiveCount = signal<number>(0);
  cachedInactiveCount = signal<number>(0);

  // Display list: when using server-side filter, apps() is already filtered
  activeAppsCount = computed(() => this.apps().filter(a => a.is_active).length);
  inactiveAppsCount = computed(() => this.apps().filter(a => !a.is_active).length);

  // Counts to show in tab labels (always visible; use cache when not on that tab)
  displayAllCount = computed(() =>
    this.activeFilter() === 'all' ? this.apps().length : this.cachedTotalCount());
  displayActiveCount = computed(() =>
    this.activeFilter() === 'all' ? this.activeAppsCount() : (this.activeFilter() === 'active' ? this.apps().length : this.cachedActiveCount()));
  displayInactiveCount = computed(() =>
    this.activeFilter() === 'all' ? this.inactiveAppsCount() : (this.activeFilter() === 'inactive' ? this.apps().length : this.cachedInactiveCount()));

  canCreateApp = computed(() => this.hasAppAdminRole());
  canUpdateApp = computed(() => this.hasAppAdminRole());
  canManageCreators = computed(() => this.hasAppAdminRole());

  canSeeOtherUsers = computed(() => this.hasAppAdminRole());

  private hasAppAdminRole(): boolean {
    const roles = this.auth.currentUser()?.roles as string[] | undefined;
    if (!roles?.length) return false;
    return roles.includes('super_admin') || roles.includes('admin');
  }

  // Modals
  showCreateModal = signal(false);
  showEditModal = signal(false);
  showDeleteModal = signal(false);
  showCreatorsModal = signal(false);
  showManageModal = signal(false);
  manageClosing = signal(false);
  appModalClosing = signal(false);
  private manageCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private appModalCloseTimer: ReturnType<typeof setTimeout> | null = null;

  // State
  selectedApp = signal<RegisteredAppResponse | null>(null);
  isSaving = signal(false);
  saveError = signal<string | null>(null);
  saveSuccess = signal<string | null>(null);

  // Danger zone (delete confirmation) state for Manage modal
  deleteConfirmText = signal('');
  isDeleteConfirmStep = signal(false);
  isDangerZoneOpen = signal(false);

  // Create/Edit modal icon selection (optional)
  pendingIconFile = signal<File | null>(null);
  pendingIconPreviewUrl = signal<string | null>(null);

  // Forms
  appForm = this.fb.group({
    app_name: ['', [Validators.required, Validators.minLength(1)]],
    website_url: [''], // optional
    app_store_id: [''],
    play_store_id: [''],
    bundle_id: [''],
    platform: ['' as '' | AppPlatform, [Validators.required]],
    category: [''],
  });
  
  creatorForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });
  
  // App Creators state
  // We need both the creator linkage info and the actual user details to show name/email
  appCreators = signal<ContentCreatorInfo[]>([]);
  appCreatorUsers = signal<Record<string, User>>({});
  isLoadingCreators = signal(false);

  // Icons and gradients
  getAppGradient(appId: string): string {
    const gradients = [
      'from-blue-500 to-[#506ef7]',
      'from-[#506ef7] to-indigo-500',
      'from-emerald-400 to-teal-500',
      'from-purple-500 to-pink-500',
      'from-amber-400 to-orange-500',
      'from-rose-400 to-red-500'
    ];
    let hash = 0;
    for (let i = 0; i < appId.length; i++) hash = appId.charCodeAt(i) + ((hash << 5) - hash);
    return gradients[Math.abs(hash) % gradients.length];
  }

  ngOnInit(): void {
    this.loadApps();
  }

  ngOnDestroy(): void {
    this.clearManageCloseTimer();
    this.clearAppModalCloseTimer();
    this.clearFilterCloseTimer();
  }

  @HostListener('document:click')
  closeFilterFromOutside(): void {
    this.closeFilterMenu();
  }

  @HostListener('document:keydown.escape')
  closeFilterFromEscape(): void {
    this.closeFilterMenu();
  }

  loadApps(): void {
    this.isLoading.set(true);
    this.errorMsg.set(null);
    const filter = this.activeFilter();
    const isActive = filter === 'all' ? undefined : filter === 'active';
    this.appService.getApps(0, 100, isActive).subscribe({
      next: (res) => {
        this.apps.set(res);
        if (filter === 'all') {
          this.cachedTotalCount.set(res.length);
          this.cachedActiveCount.set(res.filter(a => a.is_active).length);
          this.cachedInactiveCount.set(res.filter(a => !a.is_active).length);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        let msg = 'Failed to load applications.';
        if (err?.status === 403) {
          msg = 'You don\'t have permission to access applications. The server may require a specific role or the request may be missing the auth token.';
        } else if (err?.error?.detail) {
          msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
        }
        this.errorMsg.set(msg);
        this.isLoading.set(false);
      }
    });
  }

  /** Set filter and reload from API (skip=0, limit=100, is_active per filter). */
  setFilterAndLoad(filter: 'all' | 'active' | 'inactive'): void {
    this.activeFilter.set(filter);
    this.loadApps();
  }

  currentFilterLabel(): string {
    if (this.activeFilter() === 'active') return `Active (${this.displayActiveCount()})`;
    if (this.activeFilter() === 'inactive') return `Inactive (${this.displayInactiveCount()})`;
    return `All Apps (${this.displayAllCount()})`;
  }

  toggleFilterMenu(): void {
    if (this.filterOpen()) {
      this.closeFilterMenu();
      return;
    }
    this.clearFilterCloseTimer();
    this.filterClosing.set(false);
    this.filterOpen.set(true);
  }

  chooseFilter(filter: 'all' | 'active' | 'inactive'): void {
    if (filter !== this.activeFilter()) this.setFilterAndLoad(filter);
    this.closeFilterMenu();
  }

  closeFilterMenu(): void {
    if (!this.filterOpen() || this.filterClosing()) return;
    this.filterClosing.set(true);
    this.filterCloseTimer = setTimeout(() => {
      this.filterOpen.set(false);
      this.filterClosing.set(false);
      this.filterCloseTimer = null;
    }, 200);
  }

  private clearFilterCloseTimer(): void {
    if (!this.filterCloseTimer) return;
    clearTimeout(this.filterCloseTimer);
    this.filterCloseTimer = null;
  }

  fadeResults(event: AnimationCallbackEvent): void {
    const el = event.target as HTMLElement;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      event.animationComplete();
      return;
    }
    el.classList.add('app-results-fade-out');
    for (const child of el.querySelectorAll('*')) {
      for (const running of child.getAnimations()) running.cancel();
    }
    const animation = el.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 220, easing: 'ease', fill: 'forwards' },
    );
    animation.finished.then(
      () => event.animationComplete(),
      () => event.animationComplete(),
    );
  }

  // --- Manage (single screen) ---
  openManageModal(app: RegisteredAppResponse): void {
    this.clearManageCloseTimer();
    this.manageClosing.set(false);
    this.selectedApp.set(app);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.setPendingIcon(null);
    this.creatorForm.reset();
    this.deleteConfirmText.set('');
    this.isDeleteConfirmStep.set(false);
    this.isDangerZoneOpen.set(false);
    this.appForm.reset({
      app_name: app.app_name,
      website_url: app.website_url || '',
      app_store_id: app.app_store_id || '',
      play_store_id: app.play_store_id || '',
      bundle_id: app.bundle_id || '',
      platform: (app.platform === 'ios' || app.platform === 'android' || app.platform === 'both') ? app.platform : '',
      category: app.category || '',
    });
    if (this.canUpdateApp()) {
      this.appForm.enable();
    } else {
      this.appForm.disable();
    }
    this.loadCreators(app.app_id);
    this.showManageModal.set(true);
  }

  closeManageModal(): void {
    if (!this.showManageModal() || this.manageClosing()) return;
    this.manageClosing.set(true);
    this.manageCloseTimer = setTimeout(() => this.finishManageClose(), 200);
  }

  private finishManageClose(): void {
    this.manageCloseTimer = null;
    this.showManageModal.set(false);
    this.manageClosing.set(false);
    this.selectedApp.set(null);
    this.setPendingIcon(null);
    this.deleteConfirmText.set('');
    this.isDeleteConfirmStep.set(false);
    this.isDangerZoneOpen.set(false);
  }

  private clearManageCloseTimer(): void {
    if (!this.manageCloseTimer) return;
    clearTimeout(this.manageCloseTimer);
    this.manageCloseTimer = null;
  }

  /** Sync manage form values from an app (e.g. after save so Save stays disabled). */
  private patchManageFormFromApp(app: RegisteredAppResponse): void {
    this.appForm.patchValue({
      app_name: app.app_name,
      website_url: app.website_url || '',
      app_store_id: app.app_store_id || '',
      play_store_id: app.play_store_id || '',
      bundle_id: app.bundle_id || '',
      platform: (app.platform === 'ios' || app.platform === 'android' || app.platform === 'both') ? app.platform : '',
      category: app.category || '',
    }, { emitEvent: false });
  }

  openDangerZone(): void {
    if (this.isSaving()) return;
    this.isDangerZoneOpen.set(true);
    this.isDeleteConfirmStep.set(false);
    this.deleteConfirmText.set('');
  }

  closeDangerZone(): void {
    if (this.isSaving()) return;
    this.isDangerZoneOpen.set(false);
    this.isDeleteConfirmStep.set(false);
    this.deleteConfirmText.set('');
  }

  startDeleteConfirm(): void {
    if (this.isSaving()) return;
    this.isDeleteConfirmStep.set(true);
  }

  cancelDeleteConfirm(): void {
    if (this.isSaving()) return;
    this.isDeleteConfirmStep.set(false);
  }

  saveManageSettings(): void {
    const app = this.selectedApp();
    if (!app) return;
    if (this.appForm.invalid) {
      this.appForm.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    this.saveError.set(null);
    this.saveSuccess.set(null);

    const v = this.appForm.value;
    const platform = v.platform || null;
    const iconFile = this.pendingIconFile();

    this.appService.updateApp(app.app_id, {
      app_name: v.app_name || '',
      website_url: v.website_url || null,
      app_store_id: (platform === 'ios' || platform === 'both') ? (v.app_store_id || null) : null,
      play_store_id: (platform === 'android' || platform === 'both') ? (v.play_store_id || null) : null,
      bundle_id: (platform === 'ios' || platform === 'both') ? (v.bundle_id || null) : null,
      platform,
      category: v.category || null,
    }).subscribe({
      next: (updated) => {
        const after = (app: RegisteredAppResponse) => {
          this.apps.update(list => list.map(a => a.app_id === app.app_id ? app : a));
          this.selectedApp.set(app);
          this.saveSuccess.set('Saved.');
          this.isSaving.set(false);
          this.patchManageFormFromApp(app);
        };

        if (iconFile) {
          this.appService.uploadAppIcon(updated.app_id, iconFile).subscribe({
            next: (withIcon) => {
              this.setPendingIcon(null);
              after(withIcon);
            },
            error: (err) => {
              if (err?.status === 413) {
                const msg = `Icon too large. Use a file under ${this.MAX_ICON_SIZE_LABEL}, or ask the server admin to increase the upload limit (e.g. nginx client_max_body_size).`;
                this.saveError.set(msg);
                this.errorMsg.set(msg);
              }
              after(updated);
            }
          });
        } else {
          after(updated);
        }
      },
      error: (err) => {
        let msg = 'Failed to save.';
        if (err?.error?.detail) {
          msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
        }
        this.saveError.set(msg);
        this.isSaving.set(false);
      }
    });
  }

  deleteSelectedApp(): void {
    const app = this.selectedApp();
    if (!app) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    this.appService.deleteApp(app.app_id).subscribe({
      next: () => {
        this.apps.update(list => list.filter(a => a.app_id !== app.app_id));
        this.isSaving.set(false);
        this.closeManageModal();
      },
      error: (err) => {
        let msg = 'Failed to delete application.';
        if (err?.error?.detail) {
          msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
        }
        this.saveError.set(msg);
        this.isSaving.set(false);
      }
    });
  }

  // --- Create/Edit App ---
  openCreateModal(): void {
    this.clearAppModalCloseTimer();
    this.appModalClosing.set(false);
    this.appForm.reset();
    this.selectedApp.set(null);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.setPendingIcon(null);
    this.showCreateModal.set(true);
  }

  openEditModal(app: RegisteredAppResponse): void {
    this.clearAppModalCloseTimer();
    this.appModalClosing.set(false);
    this.selectedApp.set(app);
    this.appForm.reset({
      app_name: app.app_name,
      website_url: app.website_url || '',
      app_store_id: app.app_store_id || '',
      play_store_id: app.play_store_id || '',
      bundle_id: app.bundle_id || '',
      platform: (app.platform === 'ios' || app.platform === 'android' || app.platform === 'both') ? app.platform : '',
      category: app.category || '',
    });
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.setPendingIcon(null);
    this.showEditModal.set(true);
  }

  closeAppModal(): void {
    if ((!this.showCreateModal() && !this.showEditModal()) || this.appModalClosing()) return;
    this.appModalClosing.set(true);
    this.appModalCloseTimer = setTimeout(() => this.finishAppModalClose(), 200);
  }

  private finishAppModalClose(): void {
    this.appModalCloseTimer = null;
    this.showCreateModal.set(false);
    this.showEditModal.set(false);
    this.appModalClosing.set(false);
    this.selectedApp.set(null);
    this.setPendingIcon(null);
  }

  private clearAppModalCloseTimer(): void {
    if (!this.appModalCloseTimer) return;
    clearTimeout(this.appModalCloseTimer);
    this.appModalCloseTimer = null;
  }

  setPendingIcon(file: File | null): void {
    // Cleanup old preview URL
    const prev = this.pendingIconPreviewUrl();
    if (prev) URL.revokeObjectURL(prev);

    this.pendingIconFile.set(file);
    this.pendingIconPreviewUrl.set(file ? URL.createObjectURL(file) : null);
  }

  onModalIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;
    if (file.size > this.MAX_ICON_SIZE_BYTES) {
      this.saveError.set(`Icon must be ${this.MAX_ICON_SIZE_LABEL} or smaller. Selected file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      if (input) input.value = '';
      return;
    }
    this.saveError.set(null);
    this.setPendingIcon(file);
    if (input) input.value = '';
  }

  setPlatform(p: AppPlatform): void {
    const current = this.appForm.value.platform;
    if (current === p) return;
    this.appForm.patchValue({ platform: p });
  }

  isPlatform(p: AppPlatform): boolean {
    return this.appForm.value.platform === p;
  }

  /** True if the manage form or pending icon differs from the selected app (enables Save Changes). */
  hasManageFormChanges(): boolean {
    const app = this.selectedApp();
    if (!app) return false;
    const v = this.appForm.getRawValue();
    const str = (x: string | null | undefined) => (x ?? '').trim();
    if (str(v.app_name) !== str(app.app_name)) return true;
    if (str(v.website_url) !== str(app.website_url)) return true;
    if ((v.platform ?? '') !== (app.platform ?? '')) return true;
    if (str(v.app_store_id) !== str(app.app_store_id)) return true;
    if (str(v.play_store_id) !== str(app.play_store_id)) return true;
    if (str(v.bundle_id) !== str(app.bundle_id)) return true;
    if (str(v.category) !== str(app.category)) return true;
    if (this.pendingIconFile()) return true;
    return false;
  }

  readonly storeCategories: string[] = [
    // iOS App Store (primary categories)
    'Books',
    'Business',
    'Developer Tools',
    'Education',
    'Entertainment',
    'Finance',
    'Food & Drink',
    'Games',
    'Graphics & Design',
    'Health & Fitness',
    'Kids',
    'Lifestyle',
    'Magazines & Newspapers',
    'Medical',
    'Music',
    'Navigation',
    'News',
    'Photo & Video',
    'Productivity',
    'Reference',
    'Shopping',
    'Social Networking',
    'Sports',
    'Travel',
    'Utilities',
    'Weather',

    // Google Play (common categories)
    'Action',
    'Adventure',
    'Arcade',
    'Art & Design',
    'Auto & Vehicles',
    'Beauty',
    'Board',
    'Card',
    'Casual',
    'Comics',
    'Communication',
    'Creativity',
    'Dating',
    'Events',
    'House & Home',
    'Libraries & Demo',
    'Maps & Navigation',
    'Parenting',
    'Personalization',
    'Puzzle',
    'Racing',
    'Role Playing',
    'Simulation',
    'Strategy',
    'Trivia',
    'Word',
  ];

  submitAppForm(): void {
    if (this.appForm.invalid) {
      this.appForm.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    this.saveError.set(null);

    const values = this.appForm.value;
    const isEdit = !!this.selectedApp();
    const platform = values.platform || null;
    const iconFile = this.pendingIconFile();

    if (isEdit) {
      const app = this.selectedApp()!;
      this.appService.updateApp(app.app_id, {
        app_name: values.app_name || '',
        website_url: values.website_url || null,
        app_store_id: (platform === 'ios' || platform === 'both') ? (values.app_store_id || null) : null,
        play_store_id: (platform === 'android' || platform === 'both') ? (values.play_store_id || null) : null,
        bundle_id: (platform === 'ios' || platform === 'both') ? (values.bundle_id || null) : null,
        platform,
        category: values.category || null,
      }).subscribe({
        next: (updated) => {
          const afterSave = () => {
            this.saveSuccess.set('Application updated successfully.');
            this.isSaving.set(false);
            setTimeout(() => { this.closeAppModal(); this.loadApps(); }, 900);
          };

          if (iconFile) {
            this.appService.uploadAppIcon(updated.app_id, iconFile).subscribe({
              next: (withIcon) => {
                this.apps.update(list => list.map(a => a.app_id === withIcon.app_id ? withIcon : a));
                afterSave();
              },
              error: (err) => {
                if (err?.status === 413) {
                  const msg = `Icon too large. Use a file under ${this.MAX_ICON_SIZE_LABEL}, or ask the server admin to increase the upload limit (e.g. nginx client_max_body_size).`;
                  this.saveError.set(msg);
                  this.errorMsg.set(msg);
                }
                afterSave();
              },
            });
          } else {
            afterSave();
          }
        },
        error: (err) => {
          let msg = 'Failed to update application.';
          if (err?.error?.detail) {
            msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
          }
          this.saveError.set(msg);
          this.isSaving.set(false);
        }
      });
    } else {
      this.appService.createApp({
        app_name: values.app_name || '',
        website_url: values.website_url || null,
        app_store_id: (platform === 'ios' || platform === 'both') ? (values.app_store_id || null) : null,
        play_store_id: (platform === 'android' || platform === 'both') ? (values.play_store_id || null) : null,
        bundle_id: (platform === 'ios' || platform === 'both') ? (values.bundle_id || null) : null,
        platform,
        category: values.category || null,
      }).subscribe({
        next: (created) => {
          const afterCreate = () => {
            this.saveSuccess.set('Application created successfully.');
            this.isSaving.set(false);
            setTimeout(() => { this.closeAppModal(); this.loadApps(); }, 900);
          };

          if (iconFile) {
            this.appService.uploadAppIcon(created.app_id, iconFile).subscribe({
              next: (withIcon) => {
                this.apps.update(list => [withIcon, ...list]);
                afterCreate();
              },
              error: (err) => {
                if (err?.status === 413) {
                  const msg = `Icon too large. Use a file under ${this.MAX_ICON_SIZE_LABEL}, or ask the server admin to increase the upload limit (e.g. nginx client_max_body_size).`;
                  this.saveError.set(msg);
                  this.errorMsg.set(msg);
                }
                afterCreate();
              },
            });
          } else {
            afterCreate();
          }
        },
        error: (err) => {
          let msg = 'Failed to create application.';
          if (err?.error?.detail) {
            msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
          }
          this.saveError.set(msg);
          this.isSaving.set(false);
        }
      });
    }
  }

  // --- Delete App ---
  openDeleteModal(app: RegisteredAppResponse): void {
    this.selectedApp.set(app);
    this.saveError.set(null);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.selectedApp.set(null);
  }

  confirmDelete(): void {
    const app = this.selectedApp();
    if (!app) return;
    this.isSaving.set(true);
    this.appService.deleteApp(app.app_id).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeDeleteModal();
        this.loadApps();
      },
      error: (err) => {
        let msg = 'Failed to delete application.';
        if (err?.error?.detail) {
          msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
        }
        this.saveError.set(msg);
        this.isSaving.set(false);
      }
    });
  }

  // --- Content Creators ---
  openCreatorsModal(app: RegisteredAppResponse): void {
    this.selectedApp.set(app);
    this.creatorForm.reset();
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.showCreatorsModal.set(true);
    this.loadCreators(app.app_id);
  }

  closeCreatorsModal(): void {
    this.showCreatorsModal.set(false);
    this.selectedApp.set(null);
  }

  loadCreators(appId: string): void {
    this.isLoadingCreators.set(true);
    this.appService.getContentCreators(appId).subscribe({
      next: (creators) => {
        this.appCreators.set(creators);
        if (!this.canSeeOtherUsers()) {
          this.isLoadingCreators.set(false);
          return;
        }
        const userPromises = creators.map((creator: ContentCreatorInfo) => 
          new Promise<void>((resolve) => {
            if (this.appCreatorUsers()[creator.user_id]) {
              resolve(); // already have it
            } else {
              this.userService.getUserById(creator.user_id).subscribe({
                next: (u) => {
                  this.appCreatorUsers.update(dict => ({...dict, [creator.user_id]: u}));
                  resolve();
                },
                error: () => resolve() // don't break if one user fails to load
              });
            }
          })
        );
        Promise.all(userPromises).then(() => {
          this.isLoadingCreators.set(false);
        });
      },
      error: () => {
        this.isLoadingCreators.set(false);
      }
    });
  }

  addCreator(): void {
    if (this.creatorForm.invalid) {
      this.creatorForm.markAllAsTouched();
      return;
    }
    const email = this.creatorForm.value.email;
    const app = this.selectedApp();
    if (!app || !email) return;

    this.isSaving.set(true);
    this.saveError.set(null);

    // Look up user by email to get user_id
    this.userService.getUsers({ search: email }).subscribe({
      next: (res) => {
        const user = res.items.find(u => u.email === email);
        if (!user) {
          this.saveError.set(`User with email ${email} not found.`);
          this.isSaving.set(false);
          return;
        }

        this.appService.addContentCreator(app.app_id, { user_id: user.id }).subscribe({
          next: () => {
            this.creatorForm.reset();
            this.isSaving.set(false);
            this.loadCreators(app.app_id); // refresh list
          },
          error: (err) => {
            let msg = 'Failed to add content editor.';
            if (err?.error?.detail) {
              msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
            }
            this.saveError.set(msg);
            this.isSaving.set(false);
          }
        });
      },
      error: (err) => {
        this.saveError.set('Failed to look up user.');
        this.isSaving.set(false);
      }
    });
  }

  removeCreator(userId: string): void {
    const app = this.selectedApp();
    if (!app) return;
    this.isSaving.set(true);
    this.appService.removeContentCreator(app.app_id, userId).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.loadCreators(app.app_id);
      },
      error: (err) => {
        let msg = 'Failed to remove content editor.';
        if (err?.error?.detail) {
          msg = Array.isArray(err.error.detail) ? err.error.detail.map((d: any) => d.msg).join(', ') : err.error.detail;
        }
        this.saveError.set(msg);
        this.isSaving.set(false);
      }
    });
  }

  /** Max icon file size (5 MB). Must be ≤ server/nginx client_max_body_size. */
  readonly MAX_ICON_SIZE_BYTES = 5 * 1024 * 1024;
  readonly MAX_ICON_SIZE_LABEL = '5 MB';

}
